import { useCallback, useState } from 'react';

const STORAGE_KEY = 'motogo.recentChannels';
const MAX_RECENT = 5;

function readStored(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === 'string') : [];
  } catch {
    // localStorage unavailable or corrupted value — start fresh. Non-fatal.
    return [];
  }
}

function writeStored(channels: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(channels));
  } catch {
    // localStorage unavailable (private mode, quota) — recents just won't survive
    // a reload. Non-fatal.
  }
}

export interface UseRecentChannelsResult {
  /** Most-recently joined first, capped at MAX_RECENT. */
  recentChannels: string[];
  /** Record a join: moves (or inserts) the channel at the front of the list. */
  remember: (channel: string) => void;
  /** Drop a channel from the list (the little ✕ on each row). */
  forget: (channel: string) => void;
}

/**
 * Channels the rider joined before, persisted in localStorage so the join sheet can
 * offer one-tap rejoin — retyping a channel name with gloves on is the worst input
 * this app asks for.
 */
export function useRecentChannels(): UseRecentChannelsResult {
  const [recentChannels, setRecentChannels] = useState<string[]>(readStored);

  const remember = useCallback((channel: string) => {
    setRecentChannels((prev) => {
      const next = [channel, ...prev.filter((c) => c !== channel)].slice(0, MAX_RECENT);
      writeStored(next);
      return next;
    });
  }, []);

  const forget = useCallback((channel: string) => {
    setRecentChannels((prev) => {
      const next = prev.filter((c) => c !== channel);
      writeStored(next);
      return next;
    });
  }, []);

  return { recentChannels, remember, forget };
}
